import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { downloadFile } from "@/components/cards/CardActions";
import { PageHeader, Empty } from "@/components/PageHeader";
import { ManualIndex } from "@/components/manual/ManualIndex";
import { ManualSection } from "@/components/manual/ManualSection";
import { Changelog } from "@/components/manual/Changelog";
import { fmtDate } from "@/components/manual/icons";

const plain = (md) => md.replace(/[#*`>|_-]{1,3}/g, " ").replace(/\s+/g, " ");

const snippet = (text, q) => {
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return null;
  const s = Math.max(0, i - 70), e = Math.min(text.length, i + q.length + 90);
  const part = text.slice(s, e);
  const j = part.toLowerCase().indexOf(q.toLowerCase());
  return <>{s > 0 && "…"}{part.slice(0, j)}<mark className="rounded bg-amber-100 px-0.5">{part.slice(j, j + q.length)}</mark>{part.slice(j + q.length)}{e < text.length && "…"}</>;
};

const Results = ({ q, sections, onPick }) => {
  const hits = sections.map((s) => ({ s, sn: snippet(`${s.title}. ${plain(s.body)}`, q) })).filter((h) => h.sn);
  return (
    <div className="animate-rise" data-testid="manual-search-results">
      <h2 className="font-heading text-xl font-semibold text-slate-900">{hits.length} risultat{hits.length === 1 ? "o" : "i"} per "{q}"</h2>
      {hits.length === 0 ? <Empty text="Nessuna sezione contiene il testo cercato." /> : (
        <ul className="mt-4 space-y-3">
          {hits.map(({ s, sn }) => (
            <li key={s.id}>
              <Link to={`/manuale/${s.id}`} onClick={onPick} data-testid={`manual-result-${s.id}`} className="block rounded-xl border border-slate-200 bg-white p-4 transition-colors hover:border-blue-200 hover:bg-blue-50/40">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{s.group}</div>
                <div className="font-heading text-base font-semibold text-slate-900">{s.title}</div>
                <p className="mt-1 text-sm text-slate-600">{sn}</p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const PdfButton = ({ section, label, testId, variant = "ghost" }) => {
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try { await downloadFile("/manual/export", section ? { section } : {}, `manuale_${section || "completo"}.pdf`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const cls = variant === "solid" ? "bg-slate-900 text-white hover:bg-slate-700" : "border border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700";
  return (
    <button onClick={run} disabled={busy} data-testid={testId} className={`inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium transition-colors disabled:opacity-60 ${cls}`}>
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <FileDown className="h-3.5 w-3.5" />}{label}
    </button>
  );
};

export default function Manual() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { api.get("/manual").then((r) => setData(r.data)).catch(() => setData({ sections: [], versions: [] })); }, []);
  useEffect(() => { window.scrollTo({ top: 0 }); }, [id]);
  const current = useMemo(() => (id && id !== "novita" ? data?.sections.find((s) => s.id === id) : id ? null : data?.sections[0]), [data, id]);
  if (!data) return <div className="text-sm text-slate-400" data-testid="manual-loading">Caricamento manuale...</div>;
  const idx = current ? data.sections.indexOf(current) : -1;
  return (
    <div data-testid="manual-page">
      <PageHeader title="Manuale" subtitle={data.version ? `Guida operativa del pannello · versione ${data.version} del ${fmtDate(data.date)}` : "Guida operativa del pannello"}>
        <PdfButton label="Scarica manuale in PDF" testId="manual-pdf-full" variant="solid" />
        <Link to="/manuale/novita" data-testid="manual-changelog-link" className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700">Novità e versioni</Link>
      </PageHeader>
      <div className="grid grid-cols-1 gap-8 lg:grid-cols-[260px_minmax(0,1fr)]">
        <ManualIndex sections={data.sections} activeId={q ? null : (id === "novita" ? "novita" : current?.id)} q={q} setQ={setQ} />
        <div className="min-w-0 rounded-2xl border border-slate-200 bg-white p-6 sm:p-8">
          {q.trim().length >= 2 ? <Results q={q.trim()} sections={data.sections} onPick={() => setQ("")} />
            : id === "novita" ? <Changelog versions={data.versions} sections={data.sections} />
            : current ? <ManualSection key={current.id} s={current} prev={data.sections[idx - 1]} next={data.sections[idx + 1]} /> : <Empty text="Sezione non trovata o non disponibile per il tuo profilo." />}
        </div>
      </div>
    </div>
  );
}
